import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faFilter, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../Api';

const Users = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const usersPerPage = 10;

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, [currentPage, selectedRole, searchTerm]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        scope: 'management',
        page: currentPage,
        per_page: usersPerPage
      });
      
      if (selectedRole) {
        params.append('role', selectedRole);
      }
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      const response = await api.get(`/users?${params.toString()}`);
      setUsers(response.data.data || response.data);
      setTotalPages(response.data.last_page || 1);
      setTotalUsers(response.data.total || response.data.length);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    // Updated roles to match the 8 current management roles
    const currentRoles = {
      'administrateur_general_superieur': 'Business Officer',
      'administrateur_general': 'Administrateur Général',
      'resp_admin': 'Responsable Administratif',
      'chef_terrain': 'Chef de Terrain',
      'chef_terrain_superieur': 'Head of operations',
      'charge_etude': 'Chargé d\'Étude',
      'charge_etude_superieur': 'Chargé d\'Étude Supérieur',
      'resp_cati': 'Responsable CATI',
      'resp_controle': 'Responsable Contrôle'
    };
    setRoles(currentRoles);
  };


  const handleResetPassword = async (userId) => {
    const newPassword = prompt('Nouveau mot de passe (minimum 8 caractères):');
    if (newPassword && newPassword.length >= 8) {
      try {
        await api.post(`/users/${userId}/reset-password`, {
          password: newPassword
        });
        alert('Mot de passe réinitialisé avec succès');
      } catch (error) {
        console.error('Error resetting password:', error);
        if (error.response?.data?.message) {
          alert(error.response.data.message);
        } else {
          alert('Erreur lors de la réinitialisation du mot de passe');
        }
      }
    } else if (newPassword) {
      alert('Le mot de passe doit contenir au moins 8 caractères');
    }
  };


  const handleCreateUser = () => {
    navigate('/users/create');
  };


  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleRoleFilter = (role) => {
    setSelectedRole(role);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Since filtering is now done on the backend, we use users directly
  const filteredUsers = users;


  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-100';
      case 'suspended': return 'text-red-600 bg-red-100';
      case 'inactive': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active': return 'Actif';
      case 'suspended': return 'Suspendu';
      case 'inactive': return 'Inactif';
      default: return status;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Jamais';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      {/* Dotted Background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}></div>
      </div>
      
      {/* Header */}
      <header className="relative z-10 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 p-6 animate-fade-in">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 text-center sm:text-left">
          GESTION DES RESPONSABLES
        </h1>
        <button 
          onClick={handleCreateUser}
          className="w-full sm:w-auto px-6 py-3 bg-red-600 text-white rounded-xl transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 active:scale-95 active:shadow-md active:translate-y-0"
        >
          <FontAwesomeIcon icon={faPlus} className="mr-2" />
          Nouveau Responsable
        </button>
      </header>

      {/* Main content */}
      <div className="relative z-10 p-4 sm:p-6 animate-slide-up">
        <div className="max-w-7xl mx-auto">
          {/* Filters */}
          <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-4 sm:p-6 mb-4 sm:mb-6">
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-stretch sm:items-center">
              <div className="flex items-center space-x-2 flex-shrink-0">
                <FontAwesomeIcon icon={faFilter} className="text-gray-500" />
                <span className="font-medium text-gray-700 text-sm sm:text-base">Filtres:</span>
              </div>
              
              {/* Role Filter */}
              <select
                value={selectedRole}
                onChange={(e) => handleRoleFilter(e.target.value)}
                className="px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none w-full sm:w-auto text-sm sm:text-base"
              >
                <option value="">Tous les rôles</option>
                {Object.entries(roles).map(([key, value]) => (
                  <option key={key} value={key}>{value}</option>
                ))}
              </select>

              {/* Search */}
              <div className="flex-1 min-w-0">
                <input
                  type="text"
                  placeholder="Rechercher par nom ou email..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none text-sm sm:text-base"
                />
              </div>
            </div>
          </div>

          {/* Users Table */}
          <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 overflow-hidden">
            {loading ? (
              <div className="p-6 sm:p-8 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
                <p className="mt-4 text-sm sm:text-base text-gray-600">Chargement des responsables...</p>
              </div>
            ) : (
              <>
                {/* Desktop Table View */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Nom
                        </th>
                        <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Email
                        </th>
                        <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Rôle
                        </th>
                        <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Statut
                        </th>
                        <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Créé par
                        </th>
                        <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Dernière connexion
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filteredUsers.map((user) => (
                        <tr 
                          key={user.id} 
                          className="hover:bg-gray-50 transition-colors cursor-pointer"
                          onClick={() => navigate(`/users/${user.id}`)}
                        >
                          <td className="px-4 sm:px-6 py-3 sm:py-4">
                            <div className="text-sm font-medium text-gray-900 break-words">
                              {user.name}
                            </div>
                          </td>
                          <td className="px-4 sm:px-6 py-3 sm:py-4">
                            <div className="text-sm text-gray-900 break-words break-all">
                              {user.email}
                            </div>
                          </td>
                          <td className="px-4 sm:px-6 py-3 sm:py-4">
                            <div className="text-sm text-gray-900 break-words">
                              {user.role_display || roles[user.role] || user.role}
                            </div>
                          </td>
                          <td className="px-4 sm:px-6 py-3 sm:py-4">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full whitespace-nowrap ${getStatusColor(user.status)}`}>
                              {getStatusText(user.status)}
                            </span>
                          </td>
                          <td className="px-4 sm:px-6 py-3 sm:py-4 text-sm text-gray-900 break-words">
                            {user.created_by || 'Système'}
                          </td>
                          <td className="px-4 sm:px-6 py-3 sm:py-4 text-sm text-gray-900 whitespace-nowrap">
                            {formatDate(user.last_login)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card View */}
                <div className="block md:hidden">
                  <div className="divide-y divide-gray-200">
                    {filteredUsers.map((user) => (
                      <div
                        key={user.id}
                        className="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                        onClick={() => navigate(`/users/${user.id}`)}
                      >
                        <div className="space-y-3">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="text-base font-medium text-gray-900 break-words mb-1">
                                {user.name}
                              </div>
                              <div className="text-sm text-gray-600 break-words break-all">
                                {user.email}
                              </div>
                            </div>
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full whitespace-nowrap flex-shrink-0 ${getStatusColor(user.status)}`}>
                              {getStatusText(user.status)}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                              <span className="text-gray-500">Rôle:</span>
                              <div className="text-gray-900 font-medium break-words">
                                {user.role_display || roles[user.role] || user.role}
                              </div>
                            </div>
                            <div>
                              <span className="text-gray-500">Créé par:</span>
                              <div className="text-gray-900 font-medium break-words">
                                {user.created_by || 'Système'}
                              </div>
                            </div>
                            <div className="col-span-2">
                              <span className="text-gray-500">Dernière connexion:</span>
                              <div className="text-gray-900 font-medium">
                                {formatDate(user.last_login)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {filteredUsers.length === 0 && (
                  <div className="p-6 sm:p-8 text-center text-gray-500 text-sm sm:text-base">
                    Aucun responsable trouvé
                  </div>
                )}
              </>
            )}
          </div>

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-4">
              <div className="flex items-center text-xs sm:text-sm text-gray-700 whitespace-nowrap">
                  <span>
                    Affichage de {((currentPage - 1) * usersPerPage) + 1} à {Math.min(currentPage * usersPerPage, totalUsers)} sur {totalUsers} responsables
                  </span>
              </div>
              
              <div className="flex items-center justify-center sm:justify-end gap-2 flex-wrap">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-2 sm:px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                >
                  <FontAwesomeIcon icon={faChevronLeft} className="w-4 h-4" />
                </button>
                
                <div className="flex gap-1 flex-wrap justify-center">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-lg transition-colors flex-shrink-0 ${
                          currentPage === pageNum
                            ? 'bg-red-600 text-white'
                            : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-2 sm:px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                >
                  <FontAwesomeIcon icon={faChevronRight} className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Users;
